import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { format, yesterday, differenceInDays } from '../../../utils/date';

import NewFeatureRequestButton from '../../NewFeatureRequestButton';
import {
  dayPeriods,
  definedPeriods,
  definedPeriodsNames,
  getActiveOption,
  dateRangeToSubmit,
  rangePeriodFromYesterday,
  minGrowthRatePeriod,
  minGrowthRateSelectable,
} from '../configs/predefined-ranges';
import SimpleDropdown from '../../Dropdowns/SimpleDropdown';

const defaultOption = (limitedPeriodText = '', dataStartDate = null) => ({
  disabled: true,
  iconClass: 'hicon-lock mr-sm',
  tooltipText: (
    <div className="d-flex flex-column justify-content-center ignore-react-onclickoutside">
      {!limitedPeriodText && (
        <p className="mb-sm" style={{ lineHeight: '140%' }}>
          You currently do not have access to full historical data.
        </p>
      )}
      {limitedPeriodText.length > 0 &&
        dataStartDate &&
        `${limitedPeriodText} ${format(dataStartDate)}`}
      {!limitedPeriodText && (
        <NewFeatureRequestButton
          featureName="Full historical data"
          buttonText="Request Access"
          buttonClassName="btn btn-blue-bottom-shadow"
          closeOnOverlayClick={false}
          modalClassName="modal-in-tooltip"
        />
      )}
    </div>
  ),
  itemClass: 'pointer-auto cursor-not-allowed',
});

const dayPeriodsOptions = (
  maxShowedPeriod,
  maxPeriod,
  minSelectablePeriod,
  dataStartDate,
  limitedPeriodText
) => {
  const availablePeriods = dayPeriods.filter((period) => period <= maxShowedPeriod);

  return availablePeriods.map((period) => {
    const label = period > 365 ? `Last ${parseInt(period / 365, 10)} Years` : `Last ${period} Days`;
    let currentOption = defaultOption();
    if (maxPeriod >= period) currentOption = { disabled: false };
    if (dataStartDate && differenceInDays(yesterday(), dataStartDate) < period) {
      currentOption = defaultOption(limitedPeriodText, dataStartDate);
    }
    const isGrowthPeriod = minSelectablePeriod && period <= minGrowthRatePeriod;

    const periodValue = isGrowthPeriod ? minSelectablePeriod : period;
    return {
      value: periodValue.toString(),
      label,
      ...currentOption,
      disabled: currentOption.disabled
        ? true
        : period < minGrowthRateSelectable && Boolean(minSelectablePeriod),
    };
  });
};

const definedPeriodsOptions = (minSelectablePeriod, maxDate) =>
  definedPeriods
    .map((period, index) => {
      let currentOption = defaultOption();
      const realPeriod = rangePeriodFromYesterday(period, maxDate);
      currentOption = { disabled: minSelectablePeriod >= realPeriod };

      return {
        value: period,
        label: definedPeriodsNames[period],
        ...currentOption,
        itemClass: cx(currentOption.itemClass, index === 0 && 'week-divider'),
      };
    })
    .filter(Boolean);

const PredefinedRangesDropdown = ({
  onChange,
  maxPeriod,
  customTo,
  maxDate,
  customFrom,
  maxShowedPeriod,
  minSelectablePeriod = 0,
  disabled,
  dataStartDate = null,
  limitedPeriodText = null,
  unselectedStateText = 'Custom',
}) => {
  const predefinedOptions = useMemo(() => {
    const periodOptions = [
      ...dayPeriodsOptions(
        maxShowedPeriod,
        maxPeriod,
        minSelectablePeriod,
        dataStartDate,
        limitedPeriodText
      ),
      ...definedPeriodsOptions(minSelectablePeriod, maxDate),
    ];
    return [...periodOptions, { value: 'custom', label: 'Custom', itemClass: 'd-none' }].map(
      (option) => ({
        ...option,
        itemClass: option.itemClass,
      })
    );
  }, [maxShowedPeriod, maxPeriod, minSelectablePeriod]);

  const active = getActiveOption(customFrom, customTo, maxDate);

  const handleChange = (period) => {
    if (definedPeriods.includes(period)) {
      const { rangePeriod, from, to } = dateRangeToSubmit(period, maxDate);
      onChange({
        period: rangePeriod,
        date_from: from,
        date_to: to,
      });
    } else {
      onChange({
        period: parseInt(period, 10),
        date_from: null,
        date_to: null,
      });
    }
  };

  return (
    <SimpleDropdown
      selected={active.value}
      options={predefinedOptions}
      menuWidth={182}
      portal={false}
      disabled={disabled}
      unselectedStateText={unselectedStateText}
      onChange={handleChange}
    />
  );
};

PredefinedRangesDropdown.propTypes = {
  onChange: T.func.isRequired,
  maxPeriod: T.number.isRequired,
  maxShowedPeriod: T.number.isRequired,
  customTo: T.string,
  customFrom: T.string,
  disabled: T.bool,
  minSelectablePeriod: T.number,
  dataStartDate: T.string,
  limitedPeriodText: T.string,
  unselectedStateText: T.string,
  maxDate: T.oneOfType([T.string, T.object]),
};

export default PredefinedRangesDropdown;
